<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import { Button } from '@/Components/ui/button'
import LessonActions from '@/Components/Lesson/LessonActions.vue'
import ProposalInbox from '@/Components/Lesson/ProposalInbox.vue'
import {
  ArrowLeft,
  Mail,
  Calendar,
  Crown,
  Sparkles,
  Star,
  Play,
  CheckCircle,
  Clock,
  Users,
  User,
  CreditCard,
  Video,
  FileText,
  XCircle,
  AlertCircle,
} from 'lucide-vue-next'

interface Subscription {
  id: number
  tier_name: string
  tier_slug: string
  amount: number
  status: string
  status_display: string
  ended_on: string | null
  created_at: string
  cancelled_at: string | null
  cancellation_reason: string | null
  selected_slot: {
    day?: string
    time?: string
    subject_name?: string
    level_name?: string
  } | null
}

interface Student {
  id: number
  name: string
  email: string
  avatar_url: string | null
  subscribed_at: string
  subscriptions: Subscription[]
  late_cancellation_credit: number
  active_subscription: {
    id: number
    tier_name: string
    tier_slug: string
    amount: number
    pause_arranged: boolean
  } | null
}

interface Activity {
  id: number
  content_id: number
  content_title: string
  content_type: string
  thumbnail_url: string | null
  duration_minutes: number | null
  progress: number
  is_completed: boolean
  last_watched: string
}

interface Session {
  id: number
  schedule_name: string
  schedule_type: string
  enrollment_type: string
  occurrence_date: string | null
  status: string
  attended: boolean
  teacher_notes: string | null
}

interface Payment {
  id: number
  amount: number
  currency: string
  status: string
  status_display: string
  tier_name: string
  paid_at: string | null
  created_at: string
}

interface UpcomingLesson {
  id: number
  subscription_id: number
  student_name: string | null
  when: string
  status_label: string
  is_paid: boolean
  is_one_off: boolean
  can_change: boolean
  scheduled_at: string
  status: string
}

interface Proposal {
  id: number
  type: string
  type_label: string
  status: string
  message: string | null
  expires_at: string
  is_mine: boolean
  summary: string
  from_name: string
  subscription_id: number
}

const props = defineProps<{
  student: Student
  activity: Activity[]
  sessions: Session[]
  payments: Payment[]
  lessons: UpcomingLesson[]
  proposals: Proposal[]
}>()

const reload = () => router.reload({ only: ['lessons', 'proposals', 'sessions'] })

// A cancelled lesson leaves the list on reload, so its confirmation is shown here.
const lessonNotice = ref('')

function onLessonChanged(message?: string) {
  lessonNotice.value = message ?? ''
  reload()
}

/*
 * Asking for an extra lesson or a permanent change of time. Both need the
 * student's agreement, so both go out as requests rather than taking effect.
 */
const askPanel = ref<'none' | 'extra' | 'slot'>('none')
const extraAt = ref('')
const slotDay = ref('monday')
const slotTime = ref('17:00')
const askMessage = ref('')
const asking = ref(false)
const askError = ref('')

const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const activeSubscriptionId = computed(() => props.student.active_subscription?.id ?? null)
// A paused arrangement cannot take new requests, but its tutor can still end it.
const endableSubscriptionId = computed(() =>
  activeSubscriptionId.value ?? props.student.subscriptions.find(sub => sub.status === 'paused')?.id ?? null,
)

/**
 * Opening the slot panel on the time they already have, rather than on a
 * default, so a tutor moving a lesson by half an hour does not have to retype
 * the whole slot from memory.
 */
function openAsk(panel: 'extra' | 'slot') {
  askError.value = ''

  const current = props.student.subscriptions.find(sub => sub.id === activeSubscriptionId.value)
  slotDay.value = current?.selected_slot?.day?.toLowerCase() ?? 'monday'
  slotTime.value = current?.selected_slot?.time ?? '17:00'

  askPanel.value = panel
}

async function ask(payload: Record<string, unknown>) {
  if (!activeSubscriptionId.value) return

  asking.value = true
  askError.value = ''

  try {
    await window.axios.post(`/lesson-changes/arrangements/${activeSubscriptionId.value}/propose`, {
      ...payload,
      message: askMessage.value || null,
    })
    askPanel.value = 'none'
    askMessage.value = ''
    reload()
  } catch (failure: any) {
    askError.value =
      failure?.response?.data?.message ??
      Object.values(failure?.response?.data?.errors ?? {}).flat()[0] ??
      'That request did not go through. Please try again.'
  } finally {
    asking.value = false
  }
}

/*
 * Ending the lessons needs nobody's agreement, but the tutor must say why:
 * the family receives the message.
 */
const ending = ref(false)
const endReason = ref('')
const endBusy = ref(false)
const endError = ref('')

async function endLessons() {
  if (!endableSubscriptionId.value) return

  endBusy.value = true
  endError.value = ''

  try {
    await window.axios.post(`/lesson-changes/arrangements/${endableSubscriptionId.value}/end`, { reason: endReason.value })
    ending.value = false
    router.reload()
  } catch (failure: any) {
    endError.value =
      failure?.response?.data?.message ??
      Object.values(failure?.response?.data?.errors ?? {}).flat()[0] ??
      'That did not go through. Please try again.'
  } finally {
    endBusy.value = false
  }
}

const activeTab = ref<'activity' | 'sessions' | 'payments'>('activity')

const tabs = [
  { id: 'activity', label: 'Activity', icon: Play },
  { id: 'sessions', label: 'Sessions', icon: Users },
  { id: 'payments', label: 'Payments', icon: CreditCard },
]

function getTierIcon(tierSlug: string) {
  switch (tierSlug) {
    case 'gold':
      return Crown
    case 'silver':
      return Sparkles
    default:
      return Star
  }
}

function getTierColor(tierSlug: string): string {
  switch (tierSlug) {
    case 'gold':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    case 'silver':
      return 'text-gray-600 bg-gray-100 border-gray-200'
    default:
      return 'text-blue-600 bg-blue-50 border-blue-200'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-700 bg-green-50'
    case 'cancelled':
      return 'text-red-700 bg-red-50'
    case 'expired':
      return 'text-gray-700 bg-gray-100'
    case 'paused':
      return 'text-amber-700 bg-amber-50'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function getPaymentStatusColor(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'text-green-700 bg-green-50'
    case 'failed':
      return 'text-red-700 bg-red-50'
    case 'pending':
      return 'text-yellow-700 bg-yellow-50'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function formatCurrency(amount: number, currency: string = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

function formatSlot(slot: Subscription['selected_slot']): string {
  if (!slot) return ''
  const parts = []
  if (slot.day) parts.push(slot.day.charAt(0).toUpperCase() + slot.day.slice(1) + 's')
  if (slot.time) parts.push(slot.time)
  if (slot.subject_name) parts.push(slot.subject_name)
  return parts.join(' · ')
}

function getContentTypeIcon(type: string) {
  switch (type) {
    case 'video':
      return Video
    default:
      return FileText
  }
}

const completedCount = computed(() => props.activity.filter(a => a.is_completed).length)
const totalWatchTime = computed(() => {
  return props.activity.reduce((acc, a) => {
    if (a.duration_minutes && a.progress) {
      return acc + Math.round((a.duration_minutes * a.progress) / 100)
    }
    return acc
  }, 0)
})

const totalPaid = computed(() => {
  return props.payments
    .filter(p => p.status === 'succeeded')
    .reduce((acc, p) => acc + p.amount, 0)
})
</script>

<template>
  <Head :title="`${student.name} - Student Details`" />

  <DashboardLayout>
    <div class="w-full max-w-6xl space-y-6">
      <!-- Back Button -->
      <Link
        href="/teacher/students"
        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeft class="w-4 h-4" />
        Back to Students
      </Link>

      <!-- Anything waiting on an answer, from either side. -->
      <ProposalInbox :proposals="props.proposals" @changed="reload" />

      <p v-if="lessonNotice" class="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{{ lessonNotice }}</p>

      <Card v-if="props.lessons.length || endableSubscriptionId">
        <CardHeader>
          <CardTitle class="text-base">Lessons coming up</CardTitle>
          <CardDescription>
            Moving a lesson needs {{ student.name }}'s agreement. Cancelling does not and costs them nothing,
            but inside the 48-hour notice period it counts as a warning, unless their card was refused. Each
            lesson's Cancel button says which before you confirm.
          </CardDescription>
        </CardHeader>

        <CardContent class="space-y-4">
          <p v-if="!props.lessons.length" class="text-sm text-gray-500">Nothing in the diary yet.</p>

          <div v-for="lesson in props.lessons" :key="lesson.id" class="space-y-2 border-b border-gray-100 pb-4 last:border-0 last:pb-0">
            <div class="flex flex-wrap items-center gap-2 text-sm">
              <span class="font-medium text-gray-900">{{ lesson.when }}</span>
              <span v-if="lesson.is_one_off" class="rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-700">
                Extra lesson
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="lesson.is_paid ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'"
              >
                {{ lesson.is_paid ? 'Paid for' : 'Not charged yet' }}
              </span>
            </div>
            <LessonActions :lesson="lesson" is-tutor @changed="onLessonChanged" />
          </div>

          <div v-if="endableSubscriptionId" class="border-t border-gray-100 pt-4">
            <p v-if="askError" class="mb-2 text-sm text-red-700">{{ askError }}</p>

            <div v-if="askPanel === 'none' && !ending" class="flex flex-wrap gap-2">
              <template v-if="activeSubscriptionId && !student.active_subscription?.pause_arranged">
                <Button variant="outline" size="sm" @click="openAsk('extra')">Offer an extra lesson</Button>
                <Button variant="outline" size="sm" @click="openAsk('slot')">Change the weekly time</Button>
              </template>
              <Button variant="ghost" size="sm" class="text-red-600 hover:text-red-700" @click="ending = true">
                End these lessons
              </Button>
            </div>

            <div v-else-if="ending" class="space-y-3 rounded-lg border border-red-200 p-4">
              <p class="text-sm font-medium text-gray-900">End your lessons with {{ student.name }}</p>
              <p class="text-xs text-gray-500">
                Every lesson still to come is cancelled and anything {{ student.name }} has paid for them is refunded.
                A lesson inside the 48-hour notice period counts as a late cancellation, unless their card was
                refused: a warning, and the £{{ student.late_cancellation_credit.toFixed(2) }} credit
                {{ student.name }} receives comes out of your next payout.
              </p>
              <textarea
                v-model="endReason"
                rows="2"
                placeholder="Tell your student why (required)"
                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              ></textarea>
              <p v-if="endError" class="text-sm text-red-700">{{ endError }}</p>
              <div class="flex gap-2">
                <Button size="sm" variant="destructive" :disabled="endBusy || !endReason.trim()" @click="endLessons">
                  {{ endBusy ? 'Ending…' : 'End the lessons' }}
                </Button>
                <Button variant="ghost" size="sm" :disabled="endBusy" @click="ending = false">Back</Button>
              </div>
            </div>

            <div v-else class="space-y-3 rounded-lg border border-gray-200 p-4">
              <template v-if="askPanel === 'extra'">
                <p class="text-sm font-medium text-gray-900">Offer an extra lesson</p>
                <p class="text-xs text-gray-500">
                  Charged at your usual rate, and only once {{ student.name }} agrees.
                </p>
                <label class="block text-xs text-gray-700">
                  Date and time, UK time
                  <input v-model="extraAt" type="datetime-local" class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                </label>
              </template>

              <template v-else>
                <p class="text-sm font-medium text-gray-900">Change the weekly time</p>
                <p class="text-xs text-gray-500">
                  Lessons already paid for stay where they are; the rest move. The time is UK time.
                </p>
                <div class="flex gap-2">
                  <select v-model="slotDay" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" aria-label="Day">
                    <option v-for="day in weekdays" :key="day" :value="day">
                      {{ day.charAt(0).toUpperCase() + day.slice(1) }}
                    </option>
                  </select>
                  <input v-model="slotTime" type="time" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" aria-label="Time, UK time" />
                </div>
              </template>

              <textarea
                v-model="askMessage"
                rows="2"
                placeholder="Add a note (optional)"
                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              ></textarea>

              <div class="flex gap-2">
                <Button
                  size="sm"
                  :disabled="asking || (askPanel === 'extra' && !extraAt)"
                  @click="askPanel === 'extra'
                    ? ask({ type: 'extra_lesson', scheduled_at: extraAt })
                    : ask({ type: 'slot_change', day: slotDay, time: slotTime })"
                >
                  {{ asking ? 'Sending…' : 'Send request' }}
                </Button>
                <Button variant="ghost" size="sm" :disabled="asking" @click="askPanel = 'none'">Back</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Student Header -->
      <Card>
        <CardContent class="pt-6">
          <div class="grid grid-cols-[max-content_1fr] md:flex items-start gap-6">
            <!-- Avatar -->
            <div class="shrink-0">
              <img
                v-if="student.avatar_url"
                :src="student.avatar_url"
                :alt="student.name"
                class="w-20 h-20 rounded-full object-cover"
              />
              <div v-else class="w-20 h-20 rounded-full bg-linear-to-br from-teal-500 to-teal-800 flex items-center justify-center text-white font-bold text-2xl">
                {{ student.name.charAt(0).toUpperCase() }}
              </div>
            </div>

            <!-- Info -->
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-2">
                <h1 class="text-xl font-bold text-gray-900">{{ student.name }}</h1>
                <span
                  v-if="student.active_subscription"
                  :class="[getTierColor(student.active_subscription.tier_slug), 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border']"
                >
                  <component :is="getTierIcon(student.active_subscription.tier_slug)" class="w-4 h-4" />
                  {{ student.active_subscription.tier_name }}
                </span>
                <span v-else class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs md:text-sm font-medium text-gray-500 bg-gray-100 border border-gray-200">
                  Inactive
                </span>
              </div>

              <div class="flex flex-col md:flex-col md:items-center gap-3 md: text-sm text-gray-500 mb-4">
                <span class="flex items-center gap-1.5">
                  <Mail class="w-4 h-4" />
                  {{ student.email }}
                </span>
                <span class="flex items-center gap-1.5">
                  <Calendar class="w-4 h-4" />
                  Student since {{ student.subscribed_at }}
                </span>
              </div>

              <!-- Active Subscription Details -->
              <div v-if="student.active_subscription" class="flex items-center gap-4 text-sm">
                <span class="text-gray-600">
                  <strong>{{ formatCurrency(student.active_subscription.amount) }}</strong>/lesson
                </span>
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="shrink-0 grid grid-cols-3 gap-4 text-center col-span-2">
              <div class="bg-gray-50 rounded-lg px-4 py-3">
                <div class="text-2xl font-bold text-gray-900">{{ activity.length }}</div>
                <div class="text-xs text-gray-500">Videos</div>
              </div>
              <div class="bg-gray-50 rounded-lg px-4 py-3">
                <div class="text-2xl font-bold text-green-600">{{ completedCount }}</div>
                <div class="text-xs text-gray-500">Completed</div>
              </div>
              <div class="bg-gray-50 rounded-lg px-4 py-3">
                <div class="text-2xl font-bold text-gray-900">{{ sessions.length }}</div>
                <div class="text-xs text-gray-500">Sessions</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Subscription History -->
      <Card v-if="student.subscriptions.length > 0">
        <CardHeader>
          <CardTitle class="text-base">Subscription History</CardTitle>
        </CardHeader>
        <CardContent>
          <div class="space-y-3">
            <div
              v-for="sub in student.subscriptions"
              :key="sub.id"
              class="flex flex-col md:flex-row items-center justify-between gap-4  py-3 border-b border-gray-100 last:border-0"
            >
              <div class="flex items-center gap-3">
                <span :class="[getStatusColor(sub.status), 'px-2 py-1 rounded text-xs font-medium']">
                  {{ sub.status_display }}
                </span>
                <span class="font-medium text-gray-900">{{ sub.tier_name }}</span>
                <span class="text-sm text-gray-500">{{ formatCurrency(sub.amount) }}/lesson</span>
              </div>
              <div class="text-sm text-gray-500 flex items-center gap-2 md:gap-4">
                <span v-if="sub.selected_slot && formatSlot(sub.selected_slot)" class="flex items-center gap-1">
                  <Clock class="w-3.5 h-3.5" />
                  {{ formatSlot(sub.selected_slot) }}
                </span>
                <span>{{ sub.created_at }}</span>
                <span v-if="sub.ended_on">to {{ sub.ended_on }}</span>
                
                <div v-if="sub.status === 'pending'" class="flex items-center gap-2 ml-4">
                  <Link
                    :href="`/teacher/students/subscriptions/${sub.id}/accept`"
                    method="post"
                    as="button"
                    class="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Accept
                  </Link>
                  <Link
                    :href="`/teacher/students/subscriptions/${sub.id}/reject`"
                    method="post"
                    as="button"
                    class="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Reject
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Tabs -->
      <div class="border-b border-gray-200">
        <nav class="flex overflow-y-auto gap-8 pb-4">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id as 'activity' | 'sessions' | 'payments'"
            :class="[
              'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <component :is="tab.icon" class="w-4 h-4" />
            {{ tab.label }}
            <span
              v-if="tab.id === 'activity'"
              class="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {{ activity.length }}
            </span>
            <span
              v-if="tab.id === 'sessions'"
              class="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {{ sessions.length }}
            </span>
            <span
              v-if="tab.id === 'payments'"
              class="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
            >
              {{ payments.length }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div>
        <!-- Activity Tab -->
        <div v-if="activeTab === 'activity'">
          <Card>
            <CardHeader>
              <CardTitle>Content Activity</CardTitle>
              <CardDescription>
                {{ completedCount }} completed, ~{{ totalWatchTime }} min watched
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div v-if="activity.length === 0" class="text-center py-12 text-gray-400">
                <Play class="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p class="font-medium text-gray-600">No activity yet</p>
                <p class="text-sm">This student hasn't watched any content yet.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <div
                  v-for="item in activity"
                  :key="item.id"
                  class="flex items-center gap-4 py-4"
                >
                  <!-- Thumbnail -->
                  <div class="shrink-0 w-24 h-14 bg-gray-100 rounded-lg overflow-hidden relative">
                    <img
                      v-if="item.thumbnail_url"
                      :src="item.thumbnail_url"
                      :alt="item.content_title"
                      class="w-full h-full object-cover"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center">
                      <component :is="getContentTypeIcon(item.content_type)" class="w-6 h-6 text-gray-400" />
                    </div>
                    <div v-if="item.is_completed" class="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                      <CheckCircle class="w-3 h-3 text-white" />
                    </div>
                  </div>

                  <!-- Info -->
                  <div class="flex-1 min-w-0">
                    <h4 class="font-medium text-gray-900 truncate">{{ item.content_title }}</h4>
                    <div class="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span v-if="item.duration_minutes" class="flex items-center gap-1">
                        <Clock class="w-3.5 h-3.5" />
                        {{ item.duration_minutes }} min
                      </span>
                      <span>Last watched {{ item.last_watched }}</span>
                    </div>
                  </div>

                  <!-- Progress -->
                  <div class="shrink-0 w-32">
                    <div class="flex items-center justify-between text-sm mb-1">
                      <span class="text-gray-500">Progress</span>
                      <span :class="item.is_completed ? 'text-green-600 font-medium' : 'text-gray-900'">
                        {{ item.progress }}%
                      </span>
                    </div>
                    <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        :class="item.is_completed ? 'bg-green-500' : 'bg-blue-500'"
                        :style="{ width: `${item.progress}%` }"
                        class="h-full transition-all"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- Sessions Tab -->
        <div v-if="activeTab === 'sessions'">
          <Card>
            <CardHeader>
              <CardTitle>Sessions</CardTitle>
              <CardDescription>Group and 1:1 session history</CardDescription>
            </CardHeader>
            <CardContent>
              <div v-if="sessions.length === 0" class="text-center py-12 text-gray-400">
                <Users class="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p class="font-medium text-gray-600">No sessions yet</p>
                <p class="text-sm">This student hasn't attended any sessions yet.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <div
                  v-for="session in sessions"
                  :key="session.id"
                  class="flex items-center justify-between py-4"
                >
                  <div class="flex items-center gap-4">
                    <div :class="[
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      session.enrollment_type === 'group' ? 'bg-purple-100' : 'bg-blue-100'
                    ]">
                      <component
                        :is="session.enrollment_type === 'group' ? Users : User"
                        :class="session.enrollment_type === 'group' ? 'text-purple-600' : 'text-blue-600'"
                        class="w-5 h-5"
                      />
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">{{ session.schedule_name }}</h4>
                      <div class="text-sm text-gray-500">
                        {{ session.enrollment_type === 'group' ? 'Group Session' : '1:1 Session' }}
                        <span v-if="session.occurrence_date"> - {{ session.occurrence_date }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <span
                      v-if="session.attended"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-green-700 bg-green-50"
                    >
                      <CheckCircle class="w-3 h-3" />
                      Attended
                    </span>
                    <span
                      v-else-if="session.status === 'cancelled'"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-700 bg-red-50"
                    >
                      <XCircle class="w-3 h-3" />
                      Cancelled
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-700 bg-gray-100"
                    >
                      {{ session.status }}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- Payments Tab -->
        <div v-if="activeTab === 'payments'">
          <Card>
            <CardHeader>
              <div class="flex items-center justify-between">
                <div>
                  <CardTitle>Payment History</CardTitle>
                  <CardDescription>Total paid: {{ formatCurrency(totalPaid) }}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div v-if="payments.length === 0" class="text-center py-12 text-gray-400">
                <CreditCard class="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p class="font-medium text-gray-600">No payments yet</p>
                <p class="text-sm">Payment records will appear here once processed.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <div
                  v-for="payment in payments"
                  :key="payment.id"
                  class="flex items-center justify-between py-4"
                >
                  <div class="flex items-center gap-4">
                    <div :class="[
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      payment.status === 'succeeded' ? 'bg-green-100' : payment.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                    ]">
                      <CreditCard :class="[
                        'w-5 h-5',
                        payment.status === 'succeeded' ? 'text-green-600' : payment.status === 'failed' ? 'text-red-600' : 'text-gray-600'
                      ]" />
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">{{ payment.tier_name }}</h4>
                      <div class="text-sm text-gray-500">
                        {{ payment.paid_at || payment.created_at }}
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-4">
                    <span :class="[getPaymentStatusColor(payment.status), 'px-2 py-1 rounded text-xs font-medium']">
                      {{ payment.status_display }}
                    </span>
                    <span class="font-semibold text-gray-900">
                      {{ formatCurrency(payment.amount, payment.currency) }}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
